// Deskte AI floating widget — a top-bar icon (next to the 🎧 support menu)
// that pops open a floating chat panel anywhere in the app, mirroring the
// Team Chat widget but triggered from the header. The panel embeds the
// reusable DeskteAiChat.
//
// The panel is DRAGGABLE by its header (same UX as the floating Calculator)
// so it can be moved out of the way while working — Lissette 2026-06-23.
//
// Hidden when the user is already on the full "Deskte AI" page (`active`) to
// avoid two live chat instances, and when `hidden` is set.

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bot, X } from 'lucide-react';
import DeskteAiChat from './DeskteAiChat.jsx';

const PANEL_W = 420;

export default function DeskteAiWidget({ aiName = 'Deskte AI', lang = 'en', hidden = false, active = false }) {
  const es = lang === 'es';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Free-drag position. `null` until first open → falls back to the default
  // top-right anchor; once moved it sticks where the user dropped it.
  const [pos, setPos] = useState(null);
  const drag = useRef(null);

  // Compute the default anchor the first time the panel opens.
  useEffect(() => {
    if (open && pos == null && typeof window !== 'undefined') {
      const w = window.innerWidth;
      const pw = Math.min(PANEL_W, w - 24);
      setPos({ x: Math.max(12, w - pw - 16), y: 64 });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onHeaderDown = (e) => {
    if (e.target.closest('[data-no-drag]')) return;
    const base = pos || { x: 0, y: 64 };
    drag.current = { dx: e.clientX - base.x, dy: e.clientY - base.y };
    e.preventDefault();
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current) return;
      const w = window.innerWidth, h = window.innerHeight;
      setPos({
        x: Math.min(Math.max(0, e.clientX - drag.current.dx), w - 80),
        y: Math.min(Math.max(0, e.clientY - drag.current.dy), h - 40),
      });
    };
    const up = () => { drag.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  // Close the panel automatically once the user navigates onto the full page.
  useEffect(() => { if (active) setOpen(false); }, [active]);

  if (hidden || active) {
    // Still render nothing for the trigger when on the full page so there's a
    // single chat instance.
    return null;
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="dkt-hpill"
        title={aiName}
        style={{ position: 'relative' }}
      >
        <Bot size={16} />
      </button>

      {open && createPortal((
        <div
          style={{
            position: 'fixed',
            ...(pos ? { left: pos.x, top: pos.y } : { top: 64, right: 16 }),
            zIndex: 60,
            width: 'min(420px, calc(100vw - 24px))',
            height: 'min(560px, calc(100vh - 96px))',
            background: 'var(--c-bg2,#0b1622)', border: '1px solid var(--c-border2,#1f3f60)',
            borderRadius: 16, boxShadow: '0 24px 80px rgba(0,0,0,.55)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div onMouseDown={onHeaderDown} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.5rem .75rem', borderBottom: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface,#0c1c2e)', cursor: 'move', userSelect: 'none' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>
              <Bot size={15} /> {aiName}
            </span>
            <button data-no-drag title={es ? 'Cerrar' : 'Close'} onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 4, display: 'flex' }}><X size={16} /></button>
          </div>
          <div style={{ flex: 1, minHeight: 0, padding: 10 }}>
            <DeskteAiChat aiName={aiName} lang={lang} embedded />
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
